import { Injectable } from '@angular/core';
import { NbToastrService, NbWindowRef, NbWindowService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { BehaviorSubject } from 'rxjs';
import { CampaignWebsocketWindowComponent } from 'src/app/features/campaigns/campaign-table/campaign-websocket-window/campaign-websocket-window.component';
import { InsightWebsocketWindowComponent } from 'src/app/features/insights/insights-table/insight-websocket-window/insight-websocket-window.component';
import { AmplifyAPI } from 'src/app/models/amplifyApi';
import { STATUSES } from 'src/app/shared/constants/general-constants';
import { CONFIG } from '../../../environments/environment';
import { AddCampaignJob, RemoveCampaignJob } from '../../features/campaigns/redux/campaign.actions';
import { AddInsightJob, RemoveInsightJob } from '../../features/insights/redux/insights.actions';
import { AmplifyAuthService } from './amplify-auth.service';

type ResourceType = 'campaign' | 'insight';

interface RefEntry {
  job_id: string;
  status: string;
  windowRef?: NbWindowRef<any>;
  campaignId?: string;
  insightId?: number;
  insightJob?: string;
  results$?: BehaviorSubject<WebSocketResults>;
}

export interface WebSocketResults {
  status?: string;
  jobId?: string;
  job_id?: string;
  [key: string]: any;
}

@Injectable()
export abstract class BaseWebSocketService {
  public ws: WebSocket | null = null;
  public refArray: RefEntry[] = [];

  protected constructor(
    protected readonly type: ResourceType,
    protected readonly toastrService: NbToastrService,
    protected readonly windowService: NbWindowService,
    protected readonly store: Store,
    private amplifyAuthService: AmplifyAuthService
  ) {}

  public async setUp(): Promise<void> {
    try {
      const session = await this.amplifyAuthService.fetchAuthSession();
      const accessToken = session.tokens?.accessToken;
      const token = accessToken?.toString();

      const api = CONFIG.API.endpoints.find((obj: AmplifyAPI) => obj.name === 'web_socket');

      if (!api?.endpoint) {
        this.showDanger($localize`WebSocket endpoint is not configured`);
        return;
      }

      if (token) {
        this.webSocket(token, api.endpoint);
      } else {
        this.showDanger($localize`Failed to get session token`);
      }
    } catch {
      this.showDanger($localize`Unable to initialize WebSocket`);
    }
  }

  public webSocket(token: string, url: string): void {
    this.ws = new WebSocket(`${url}?token=${token}`);

    this.ws.onerror = () => {
      this.showDanger($localize`WebSocket Error - Contact support if persistent`);
    };

    this.ws.onclose = () => {};

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event);
    };
  }

  public sendMessage(message: string, item?: string | number | undefined): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.showDanger($localize`WebSocket is not connected`);
      return;
    }

    let obj: RefEntry;

    if (this.type === 'campaign') {
      obj = {
        campaignId: item as string | undefined,
        job_id: message,
        status: '',
        windowRef: undefined,
        insightJob: undefined
      };

      this.refArray.push(obj);
      this.saveToRedux(obj);
      this.ws.send(JSON.stringify({ action: 'getstatus', jobId: message, campaign_id: item }));
    } else {
      obj = {
        insightId: item as number | undefined,
        job_id: message,
        status: '',
        windowRef: undefined,
        insightJob: undefined
      };

      this.refArray.push(obj);
      this.saveToRedux(obj);
      this.ws.send(JSON.stringify({ action: 'getstatus', jobId: message, insight_id: item }));
    }
  }

  public saveToRedux(obj: RefEntry): void {
    const job = { ...obj };

    if (this.type === 'campaign') {
      this.store.dispatch(new AddCampaignJob(job));
    } else {
      this.store.dispatch(new AddInsightJob(job));
    }
  }

  public removeFromRedux(jobId: string): void {
    if (this.type === 'campaign') {
      this.store.dispatch(new RemoveCampaignJob(jobId));
    } else {
      this.store.dispatch(new RemoveInsightJob(jobId));
    }
  }

  public closeWindow(jobId: string): void {
    const index = this.refArray.findIndex((element) => element.job_id === jobId);

    if (index !== -1 && this.refArray[index].windowRef) {
      this.refArray[index].windowRef?.close();
    }

    this.refArray = this.refArray.filter((element) => element.job_id !== jobId);
    this.removeFromRedux(jobId);

    this.toastrService.show('', $localize`Successfully stopped ${jobId}`, {
      status: 'success'
    });
  }

  private handleMessage(event: MessageEvent): void {
    let results: WebSocketResults;

    try {
      results = JSON.parse(event.data);
    } catch {
      this.showDanger($localize`Invalid WebSocket message received`);
      return;
    }

    if (!results.status) {
      return;
    }

    let index = -1;

    if (results.status === STATUSES.SUCCESS || results.status === STATUSES.FAIL) {
      index = this.refArray.findIndex((element) => element.job_id === results.jobId);
      if (results.jobId) {
        this.removeFromRedux(results.jobId);
      }
    } else {
      index = this.refArray.findIndex((element) => element.job_id === results.job_id);
    }

    if (index === -1) {
      return;
    }

    this.refArray[index].status = results.status;
    this.openOrUpdateWindow(index, results);
  }

  private openOrUpdateWindow(index: number, results: WebSocketResults): void {
    const isCampaign = this.type === 'campaign';

    const title = isCampaign
      ? $localize`Campaign: ${this.refArray[index].campaignId} [${results.status}]`
      : $localize`Insight: ${this.refArray[index].insightId} [${results.status}]`;

    if (this.refArray[index].windowRef) {
      this.refArray[index].windowRef.config.title = title;
      this.refArray[index].results$?.next({ ...results });
      return;
    }

    const results$ = new BehaviorSubject<WebSocketResults>({ ...results });
    this.refArray[index].results$ = results$;

    const context = isCampaign
      ? {
          campaign_id: this.refArray[index].campaignId,
          results$
        }
      : {
          insight_id: this.refArray[index].insightId,
          results$
        };

    this.refArray[index].windowRef = this.windowService.open(
      isCampaign ? CampaignWebsocketWindowComponent : InsightWebsocketWindowComponent,
      {
        title,
        context,
        buttons: { fullScreen: false }
      }
    );

    this.refArray[index].windowRef?.maximize();
    this.refArray[index].windowRef?.minimize();
  }

  private showDanger(message: string): void {
    this.toastrService.show('', message, { status: 'danger' });
  }
}

@Injectable({ providedIn: 'root' })
export class CampaignWebSocketService extends BaseWebSocketService {
  constructor(
    toastrService: NbToastrService,
    windowService: NbWindowService,
    store: Store,
    amplifyAuthService: AmplifyAuthService
  ) {
    super('campaign', toastrService, windowService, store, amplifyAuthService);
  }
}

@Injectable({ providedIn: 'root' })
export class InsightWebSocketService extends BaseWebSocketService {
  constructor(
    toastrService: NbToastrService,
    windowService: NbWindowService,
    store: Store,
    amplifyAuthService: AmplifyAuthService
  ) {
    super('insight', toastrService, windowService, store, amplifyAuthService);
  }
}
